/**
 * @file use-integration-form.ts
 * @description Состояние и сохранение формы интеграции для
 * `IntegrationEditDialog` (phase19.29: вынесено из диалога, чтобы он
 * уложился в 200 строк; логика не менялась).
 *
 * Заполняет форму из редактируемого экземпляра, грузит список магазинов для
 * скоупа, проверяет обязательные поля и создаёт/обновляет интеграцию.
 * Ошибка сервера — в поле `error` и тостом.
 *
 * phase19.28: `defaultShopId` — новая настройка сразу для магазина
 * (создание из вкладки «Интеграции» карточки магазина).
 */

import { useEffect, useMemo, useState } from "react";

import { useNotify } from "../ui/NotifyProvider";
import type { SelectFieldOption } from "../ui/SelectField";
import { ApiError } from "../../lib/api-client";
import {
  INTEGRATION_KIND_META,
  platformIntegrations,
  type PlatformIntegrationDto,
  type PlatformIntegrationKind,
  type PlatformIntegrationStatus,
} from "../../lib/api-platform-integrations";
import { shops, type ShopPublicDto } from "../../lib/api-shops";

export function useIntegrationForm(
  kind: PlatformIntegrationKind,
  integration: PlatformIntegrationDto | null,
  onSaved: () => void,
  defaultShopId?: string,
) {
  const notify = useNotify();
  const meta = INTEGRATION_KIND_META[kind];

  const [name, setName] = useState("");
  const [shopId, setShopId] = useState<string | "">("");
  const [allShops, setAllShops] = useState<ShopPublicDto[]>([]);
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [config, setConfig] = useState<Record<string, unknown>>({});
  const [isPrimary, setIsPrimary] = useState(false);
  const [status, setStatus] = useState<PlatformIntegrationStatus>("ACTIVE");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void shops
      .list()
      .then(setAllShops)
      .catch(() => setAllShops([]));
  }, []);

  useEffect(() => {
    setError(null);
    if (integration) {
      setName(integration.name);
      setShopId(integration.shopId ?? "");
      setCredentials({});
      setConfig((integration.config as Record<string, unknown>) ?? {});
      setIsPrimary(integration.isPrimary);
      setStatus(integration.status === "NOT_CONFIGURED" ? "ACTIVE" : integration.status);
    } else {
      setName("");
      setShopId(defaultShopId ?? "");
      setCredentials({});
      setConfig({});
      setIsPrimary(false);
      setStatus("ACTIVE");
    }
  }, [integration, defaultShopId]);

  // Варианты скоупа: платформенная + магазины (неактивные — с пометкой).
  const scopeOptions = useMemo<SelectFieldOption[]>(
    () => [
      { value: "", label: "Вся платформа (все магазины)" },
      ...allShops.map((s) => ({
        value: s.id,
        label: s.isActive ? `Магазин «${s.displayName}»` : `Магазин «${s.displayName}» (неактивен)`,
      })),
    ],
    [allShops],
  );

  const hasMissingRequired = useMemo(() => {
    if (integration) return false;
    return meta.credentialFields
      .filter((f) => f.required)
      .some((f) => {
        const value = credentials[f.key];
        return !value || value.trim().length === 0;
      });
  }, [credentials, meta.credentialFields, integration]);

  async function onSubmit() {
    setError(null);
    if (!name.trim()) {
      setError("Укажите имя экземпляра.");
      return;
    }
    if (!integration && hasMissingRequired) {
      setError("Заполните обязательные ключи доступа.");
      return;
    }
    setSaving(true);
    try {
      const credentialsTrimmed: Record<string, string> = {};
      for (const [k, v] of Object.entries(credentials)) {
        const t = v.trim();
        if (t.length > 0) credentialsTrimmed[k] = t;
      }
      const shopIdValue = shopId === "" ? null : shopId;
      if (integration) {
        await platformIntegrations.update(integration.id, {
          shopId: shopIdValue,
          name: name.trim(),
          credentials:
            Object.keys(credentialsTrimmed).length > 0 ? credentialsTrimmed : undefined,
          config,
          isPrimary,
          status,
        });
      } else {
        await platformIntegrations.create({
          kind,
          shopId: shopIdValue,
          name: name.trim(),
          credentials:
            Object.keys(credentialsTrimmed).length > 0 ? credentialsTrimmed : undefined,
          config,
          isPrimary,
        });
      }
      notify.success(integration ? "Сохранено" : "Интеграция подключена");
      onSaved();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Не удалось сохранить";
      setError(msg);
      notify.error(msg);
    } finally {
      setSaving(false);
    }
  }

  return {
    meta,
    name,
    setName,
    shopId,
    setShopId,
    scopeOptions,
    credentials,
    setCredentials,
    config,
    setConfig,
    isPrimary,
    setIsPrimary,
    status,
    setStatus,
    saving,
    error,
    onSubmit,
  };
}
